import { Injectable, OnDestroy } from '@angular/core';

/** Fields that raise a software keyboard. Buttons and checkboxes don't. */
const TEXT_ENTRY = 'input:not([type=checkbox]):not([type=radio]):not([type=button]):not([type=submit]):not([type=file]), textarea, [contenteditable=""], [contenteditable="true"]';

/** Breathing room above/below the field, so it doesn't sit flush against the
 *  keyboard or a sticky header and read as clipped. */
const MARGIN_PX = 16;

/**
 * How long to wait for the keyboard before revealing anyway. The visualViewport
 * resize is the reliable signal and normally lands well inside this; the timer
 * only covers browsers that raise a keyboard without resizing anything, where
 * no event is ever coming.
 */
const SETTLE_FALLBACK_MS = 350;

/**
 * How long after the last viewport change to wait before measuring.
 *
 * Not cosmetic. The shell's height comes from a signal that Angular applies on
 * its own schedule, so at the instant the resize fires the layout is still the
 * old, taller one: the scroll container isn't overflowing yet, and
 * scrollIntoView correctly does nothing because there is nowhere to scroll to.
 * Measured that exact no-op in a browser before adding this.
 *
 * Because every resize restarts the timer, this also doubles as "wait until
 * the keyboard animation stops changing the viewport" rather than firing once
 * per intermediate frame.
 */
const LAYOUT_SETTLE_MS = 120;

/**
 * Scrolls a newly focused field back into view once the software keyboard has
 * opened.
 *
 * Browsers do attempt this themselves, but they decide before the keyboard is
 * up — so the field is scrolled to a position that the keyboard then covers,
 * and inside a height-clamped shell whose inner panes do their own scrolling
 * (the dashboard) they frequently pick the wrong container or give up. The
 * onboarding crawl URL field was the reported case: tap it and the keyboard
 * takes the bottom half of the screen with the field somewhere underneath.
 *
 * Waiting for the viewport to actually change is the whole trick — measuring
 * before the keyboard is up just reproduces the browser's own mistake.
 *
 * App-wide (instantiated by AppComponent) rather than dashboard-only, because
 * login, register, support and onboarding all have the same problem and none
 * of them share a layout. Cheap when idle: two listeners and no work until
 * something focusable is focused.
 */
@Injectable({ providedIn: 'root' })
export class FocusScrollService implements OnDestroy {
  private pending: HTMLElement | null = null;
  private settleTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly onFocusIn = (event: FocusEvent) => {
    const target = event.target as HTMLElement | null;
    if (!target?.matches?.(TEXT_ENTRY)) return;
    this.pending = target;
    this.scheduleReveal(SETTLE_FALLBACK_MS);
  };

  // A field can lose focus before the keyboard ever appears (a fast tap
  // through to something else). Revealing it then would scroll the page for a
  // field the user has already left.
  private readonly onFocusOut = () => {
    this.pending = null;
    this.clearSettleTimer();
  };

  private readonly onViewportChange = () => {
    // Restarted on every change, so the reveal lands once the keyboard has
    // stopped animating and the shell has reflowed — not on each frame of it.
    if (this.pending) this.scheduleReveal(LAYOUT_SETTLE_MS);
  };

  // Both guards, not just one: the app prerenders 50 routes at build time, and
  // there is no requirement that a server-side DOM shim providing `document`
  // also provides `window`.
  private readonly active = typeof document !== 'undefined' && typeof window !== 'undefined';

  constructor() {
    if (!this.active) return;
    document.addEventListener('focusin', this.onFocusIn);
    document.addEventListener('focusout', this.onFocusOut);
    window.visualViewport?.addEventListener('resize', this.onViewportChange);
  }

  ngOnDestroy(): void {
    if (!this.active) return;
    document.removeEventListener('focusin', this.onFocusIn);
    document.removeEventListener('focusout', this.onFocusOut);
    window.visualViewport?.removeEventListener('resize', this.onViewportChange);
    this.clearSettleTimer();
  }

  private scheduleReveal(delayMs: number): void {
    this.clearSettleTimer();
    this.settleTimer = setTimeout(() => this.reveal(), delayMs);
  }

  private clearSettleTimer(): void {
    if (this.settleTimer === null) return;
    clearTimeout(this.settleTimer);
    this.settleTimer = null;
  }

  /**
   * Bring the pending field inside the visible band, if it isn't already.
   *
   * The no-op path matters as much as the scroll: on a desktop browser (and
   * on mobile whenever the browser got it right on its own) the field is
   * already in view, and scrolling anyway would yank the page under someone
   * who simply tabbed between fields.
   */
  private reveal(): void {
    const el = this.pending;
    this.pending = null;
    this.clearSettleTimer();

    // Focus can move, or the field can be removed, between the tap and the
    // keyboard finishing its animation.
    if (!el || !el.isConnected || document.activeElement !== el) return;

    const vv = window.visualViewport;
    // getBoundingClientRect is in layout-viewport coordinates, and the visual
    // viewport sits at offsetTop within it — so that offset is part of where
    // the visible band actually starts, not something to subtract.
    const bandTop = vv ? vv.offsetTop : 0;
    const bandBottom = bandTop + (vv?.height ?? window.innerHeight);

    const rect = el.getBoundingClientRect();
    const visible = rect.top >= bandTop + MARGIN_PX && rect.bottom <= bandBottom - MARGIN_PX;
    if (visible) return;

    // 'center' rather than 'nearest': nearest parks the field flush against
    // the edge of the band, which on a phone means directly on top of the
    // keyboard, with nothing of the surrounding form visible for context.
    //
    // Instant, not smooth. A smooth scroll starting here finishes well after
    // the keyboard has settled, so it reads as a delayed lurch rather than
    // part of the same motion — and it is the fragile option besides, being
    // interruptible by the browser's own scroll adjustments while the
    // keyboard animates (it silently did nothing at all in a headless Chrome
    // check, where the instant form scrolled correctly).
    el.scrollIntoView({ block: 'center', behavior: 'auto' });
  }
}
